import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { deriveRoleFromEmail, useAuth } from "../context/AuthContext";
import {
    challengeMFA,
    listMFAFactors,
    verifyMFA,
} from "../services/mfaService";
import { getSupabaseClient } from "../services/supabaseClient";
import "../styles/auth.css";

const formatCountdown = (seconds) => {
    if (!Number.isFinite(seconds) || seconds <= 0) {
        return "0s";
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins <= 0) {
        return `${secs}s`;
    }
    return `${mins}m ${secs}s`;
};

const selectMfaFactor = (data) => {
    if (!data) {
        return null;
    }
    const factors = data.totp || data.all || data.factors || [];
    if (!Array.isArray(factors)) {
        return null;
    }
    return factors.find((factor) => factor.status === "verified") || null;
};

export default function AuthPage() {
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [localError, setLocalError] = useState(null);
    const [lockoutUntil, setLockoutUntil] = useState(null);
    const [lockoutRemaining, setLockoutRemaining] = useState(0);
    const [mfaCode, setMfaCode] = useState("");
    const [mfaError, setMfaError] = useState(null);
    const [mfaAttempts, setMfaAttempts] = useState(0);
    const [mfaChallenge, setMfaChallenge] = useState(null);
    const [pendingSignIn, setPendingSignIn] = useState(null);
    const [resendStatus, setResendStatus] = useState(null);

    const {
        signIn,
        signOut,
        user,
        loading,
        error,
        setError,
        authEnabled,
        emailVerificationPending,
        pendingVerificationEmail,
        resendVerificationEmail,
        completeSignIn,
        recoveryUser,
    } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const redirectTo = useMemo(
        () => location.state?.from?.pathname || "/",
        [location.state]
    );

    useEffect(() => {
        if (recoveryUser) {
            navigate("/auth/reset-password", { replace: true });
            return;
        }
        if (authEnabled && user && !mfaChallenge && !emailVerificationPending) {
            navigate(redirectTo, { replace: true });
        }
    }, [user, redirectTo, navigate, authEnabled, mfaChallenge, emailVerificationPending, recoveryUser]);

    useEffect(() => {
        if (!lockoutUntil) {
            setLockoutRemaining(0);
            return;
        }

        const updateRemaining = () => {
            const remaining = Math.max(
                0,
                Math.ceil((lockoutUntil - Date.now()) / 1000)
            );
            setLockoutRemaining(remaining);
            if (remaining <= 0) {
                setLockoutUntil(null);
            }
        };

        updateRemaining();
        const interval = setInterval(updateRemaining, 1000);
        return () => clearInterval(interval);
    }, [lockoutUntil]);

    const handleResendVerification = async () => {
        setResendStatus(null);
        try {
            await resendVerificationEmail(pendingVerificationEmail || email);
            setResendStatus("Verification email sent.");
        } catch (err) {
            setResendStatus(err?.message || "Unable to resend verification email.");
        }
    };

    const handleMfaVerify = async (event) => {
        event.preventDefault();
        if (!mfaChallenge) {
            return;
        }

        const cleaned = mfaCode.replace(/\s+/g, "");
        if (!/^\d{6}$/.test(cleaned)) {
            setMfaError("Enter the 6-digit code from your authenticator app.");
            return;
        }

        setSubmitting(true);
        setMfaError(null);

        try {
            const verified = await verifyMFA({
                factorId: mfaChallenge.factorId,
                challengeId: mfaChallenge.challengeId,
                code: cleaned,
            });

            const supabase = getSupabaseClient();
            let nextSession = verified?.session;
            let nextUser = verified?.user || pendingSignIn?.user;
            if (!nextSession) {
                const { data } = await supabase.auth.getSession();
                nextSession = data?.session || null;
                nextUser = data?.session?.user || nextUser;
            }

            await completeSignIn({
                session: nextSession,
                user: nextUser,
                email: pendingSignIn?.email,
            });

            setMfaChallenge(null);
            setPendingSignIn(null);
            setMfaCode("");
            setMfaAttempts(0);
            navigate(redirectTo, { replace: true });
        } catch (err) {
            const nextAttempts = mfaAttempts + 1;
            setMfaAttempts(nextAttempts);
            if (nextAttempts >= 3) {
                setMfaError("Too many invalid codes. Please sign in again.");
                await signOut().catch(() => {});
                setMfaChallenge(null);
                setPendingSignIn(null);
                setMfaCode("");
                setMfaAttempts(0);
            } else {
                setMfaError(err?.message || "Invalid MFA code. Try again.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let signedIn = false;
        setSubmitting(true);
        setLocalError(null);
        setError(null);
        setResendStatus(null);
        try {
            if (!authEnabled) {
                throw new Error(
                    "Supabase auth is not configured. Add REACT_APP_SUPABASE_URL/REACT_APP_SUPABASE_ANON_KEY (or NEXT_PUBLIC_SUPABASE_URL/NEXT_PUBLIC_SUPABASE_ANON_KEY) to use authentication."
                );
            }
            if (lockoutRemaining > 0) {
                throw new Error("Account is temporarily locked.");
            }

            const data = await signIn({ email, password });
            signedIn = true;
            const authUser = data?.user;
            if (!authUser) {
                throw new Error("Unable to load user profile.");
            }

            let profileRole = null;
            try {
                const supabase = getSupabaseClient();
                const { data: profileData, error: profileError } = await supabase
                    .from("profiles")
                    .select("role")
                    .eq("id", authUser.id)
                    .maybeSingle();
                if (!profileError) {
                    profileRole = profileData?.role || null;
                }
            } catch (error) {
                profileRole = null;
            }

            const role =
                profileRole ||
                authUser.user_metadata?.role ||
                deriveRoleFromEmail(authUser.email);
            if (role === "internal_staff" && !authUser.email_confirmed_at) {
                await signOut().catch(() => {});
                throw new Error("Please verify your email before signing in.");
            }

            const factorList = await listMFAFactors();
            const factor = selectMfaFactor(factorList);
            if (factor) {
                const challenge = await challengeMFA({ factorId: factor.id });
                setMfaChallenge({
                    factorId: factor.id,
                    challengeId: challenge?.id,
                });
                setMfaCode("");
                setMfaError(null);
                setMfaAttempts(0);
                setPendingSignIn({
                    session: data?.session,
                    user: authUser,
                    email: authUser.email,
                });
                return;
            }

            await completeSignIn({
                session: data?.session,
                user: authUser,
                email: authUser.email,
            });
            navigate(redirectTo, { replace: true });
        } catch (err) {
            if (signedIn) {
                await signOut().catch(() => {});
            }
            if (err?.status === 429) {
                const remainingSeconds = Number(err?.remainingSeconds || 0);
                if (remainingSeconds > 0) {
                    setLockoutUntil(Date.now() + remainingSeconds * 1000);
                }
            }
            const message = err?.message || "Unable to authenticate.";
            setLocalError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const canSubmit = useMemo(() => {
        if (!email || !password) {
            return false;
        }
        return lockoutRemaining <= 0;
    }, [email, password, lockoutRemaining]);

    return (
        <main className="auth-page">
            <div className="auth-shell">
            <div className="auth-brand">
                <img src="/images/be+accredited+group.webp" alt="Be Accredited Group"
                    width="1473" height="304" loading="eager" />
            </div>
            <div className="auth-layout">
                <section className="auth-introduction" aria-label="About Password Manager">
                    <p className="eyebrow">PASSWORD MANAGER</p>
                    <h2>Customer logins.<br />One place to find them.</h2>
                    <p>Access and manage customer accreditation credentials with your Be Accredited account.</p>
                    <div className="auth-guidance">
                        <span className="auth-guidance-mark" aria-hidden="true">01</span>
                        <div><h3>Keep credentials together</h3><p>Find the company and provider you need without searching through notes or emails.</p></div>
                    </div>
                    <div className="auth-guidance">
                        <span className="auth-guidance-mark" aria-hidden="true">02</span>
                        <div><h3>Share a link, not a password</h3><p>Use the credential’s page link in CRM so passwords stay in the app.</p></div>
                    </div>
                </section>
            <section className="auth-card" aria-labelledby="auth-title">
                <p className="auth-card-eyebrow">YOUR ACCOUNT</p>
                <h1 id="auth-title">Welcome back</h1>
                <p className="auth-description">Sign in to access your credentials.</p>

                <form className="auth-form" onSubmit={handleSubmit}>
                    {location.state?.passwordReset && (
                        <p className="helper" role="status">Password reset successfully. Sign in with your new password.</p>
                    )}
                    <label className="form-field">
                        <span>Email</span>
                        <input
                            type="email"
                            name="email"
                            autoComplete="email"
                            placeholder="you@company.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </label>
                    <div className="form-field">
                        <label htmlFor="auth-password">Password</label>
                        <div className="auth-password-control">
                            <input id="auth-password" type={passwordVisible ? "text" : "password"}
                                name="password" autoComplete="current-password"
                                value={password} onChange={(e) => setPassword(e.target.value)} required />
                            <button type="button" className="auth-password-toggle" aria-controls="auth-password"
                                aria-label={passwordVisible ? "Hide password" : "Show password"}
                                aria-pressed={passwordVisible} onClick={() => setPasswordVisible(!passwordVisible)}>
                                {passwordVisible ? "Hide" : "Show"}
                            </button>
                        </div>
                    </div>

                    <Link className="auth-recovery-link" to="/auth/forgot-password" state={{ email }}>
                        Forgot password?
                    </Link>

                    {lockoutRemaining > 0 && (
                        <p className="warning-message">
                            Too many failed login attempts. Try again in{" "}
                            {formatCountdown(lockoutRemaining)}.
                        </p>
                    )}

                    {emailVerificationPending && (
                        <p className="warning-message">
                            Please check your email to verify your account before signing in.
                        </p>
                    )}

                    {(localError || error) && (
                        <p className="error-message" role="alert">{localError || error}</p>
                    )}

                    {emailVerificationPending && (
                        <button
                            className="btn ghost"
                            type="button"
                            onClick={handleResendVerification}
                        >
                            Resend verification email
                        </button>
                    )}
                    {resendStatus && <p className="helper" role="status">{resendStatus}</p>}

                    <button
                        className="btn primary"
                        type="submit"
                        disabled={submitting || loading || !canSubmit}
                    >
                        {submitting ? "Signing in..." : "Sign in"}
                    </button>
                </form>
                <p className="auth-access-note">Need access? Please contact your Be Accredited team.</p>

                {!authEnabled && <div className="footnote">
                        <button
                            type="button"
                            className="btn ghost"
                            onClick={() => navigate("/")}
                        >
                            Continue without auth (dev only)
                        </button>
                </div>}
            </section>
            </div>
            </div>

            {mfaChallenge && (
                <div className="mfa-modal">
                    <div className="modal-overlay" />
                    <div className="modal-card">
                        <div className="modal-header">
                            <h2>Two-factor authentication</h2>
                        </div>
                        <p className="modal-body">
                            Enter the 6-digit code from your authenticator app to finish
                            signing in.
                        </p>
                        <form onSubmit={handleMfaVerify} className="auth-form">
                            <label className="form-field">
                                <span>Authentication code</span>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    value={mfaCode}
                                    onChange={(e) => setMfaCode(e.target.value)}
                                    placeholder="123456"
                                />
                            </label>
                            {mfaError && <p className="error-message">{mfaError}</p>}
                            <button
                                className="btn primary"
                                type="submit"
                                disabled={submitting}
                            >
                                Verify code
                            </button>
                            <p className="smallprint">
                                Attempt {mfaAttempts + 1} of 3
                            </p>
                        </form>
                    </div>
                </div>
            )}
        </main>
    );
}
